import api from '@/api'

/**
 * Ad-hoc ADA scan of any URL (Settings → Audits, and the Home shortcut).
 *
 * Hits POST /juniper/ada-auditor/test-scan, which runs the engines and returns
 * the findings inline — nothing is persisted, so there is no job to poll and no
 * report to clean up. Rejects with a ready-to-display message: a 502 still
 * carries the per-engine reasons, and those are what actually explain a failure.
 *
 * @param {{url: string, engines: string[], applyWarnings?: boolean}} opts
 * @returns {Promise<object>} the test-scan response body
 */
export async function runAdaTestScan({ url, engines, applyWarnings = false }) {
	try {
		const res = await api.post('/juniper/ada-auditor/test-scan', {
			url,
			engines,
			apply_warnings: applyWarnings,
		})
		return res.data
	} catch (e) {
		throw new Error(adaTestScanError(e))
	}
}

/** Turn an axios failure from the endpoint into one human-readable line. */
export function adaTestScanError(e) {
	const data = (e.response && e.response.data) || {}
	const engineErr = (data.engines || []).filter(x => x.error).map(x => `${x.engine}: ${x.error}`)
	if (engineErr.length) {
		return `${data.message || 'Scan failed.'} (${engineErr.join('; ')})`
	}
	// Laravel validation (422) reports per-field; the URL is the only field worth surfacing.
	if (data.errors && data.errors.url) {
		return data.errors.url[0]
	}
	return data.message || 'Scan failed. Check the URL and that ada-engine is reachable.'
}

/** The engines the tool offers, in the order a real client audit runs them. */
export const ADA_TEST_ENGINES = [
	{ value: 'twac', label: 'TWAC', hint: 'Town Web checks — broken links, linked PDF accessibility' },
	{ value: 'ibm', label: 'IBM Equal Access', hint: 'Code-level WCAG 2.2 AA' },
	{ value: 'axe', label: 'AXE', hint: 'Deque axe-core engine' },
	{ value: 'wave', label: 'WAVE', hint: 'WebAIM — external paid API, spends credits per scan' },
]

/** Default selection: the engines a normal client audit runs (WAVE costs credits). */
export const ADA_TEST_DEFAULT_ENGINES = ['twac', 'ibm', 'axe']
